import type { SharedAiBuddyMessage } from '../components/SharedAiBuddyCard';

export const INTERACTIVE_BOOK_CHAT_STORAGE_PREFIX = 'interactive-book-chat:v1';
export const INTERACTIVE_BOOK_CHAT_ANON_SCOPE_KEY = 'interactive-book-chat-anon-scope';
export const INTERACTIVE_BOOK_CHAT_ACTIVE_USER_SCOPE = 'interactive-book-chat-active-user-scope';

export const CHAT_SESSION_MAX_MESSAGES = 30;
export const CHAT_SESSION_MAX_MESSAGE_CHARS = 8000;
/** Survives tab/browser close; bounded retention for preview/dev chat history. */
export const CHAT_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type PersistedChatMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
};

export type PersistedInteractiveBookChat = {
  version: 1;
  bookId: string;
  interactionId: string;
  userScope: string;
  messages: PersistedChatMessage[];
  savedAt: number;
};

export type InteractiveBookChatSessionScope = {
  bookId: string;
  interactionId: string;
};

export type RestoredInteractiveBookChat = {
  uiMessages: SharedAiBuddyMessage[];
  apiHistory: Array<{ role: 'user' | 'assistant'; content: string }>;
};

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'key' | 'length'>;

let chatStorageRef: StorageLike | null | undefined;

function getChatStorage(): StorageLike | null {
  if (chatStorageRef !== undefined) return chatStorageRef;
  if (typeof window === 'undefined' || !window.localStorage) {
    chatStorageRef = null;
    return null;
  }
  chatStorageRef = window.localStorage;
  return chatStorageRef;
}

/** @internal test hook */
export function __setInteractiveBookChatStorageForTests(storage: StorageLike | null): void {
  chatStorageRef = storage;
}

function migrateLegacySessionStorageChat(): void {
  if (typeof window === 'undefined') return;
  let sessionStorageApi: Storage | null = null;
  try {
    sessionStorageApi = window.sessionStorage;
  } catch {
    return;
  }
  const local = getChatStorage();
  if (!local || !sessionStorageApi) return;

  try {
    const keys: string[] = [];
    for (let index = 0; index < sessionStorageApi.length; index += 1) {
      const key = sessionStorageApi.key(index);
      if (
        key &&
        (key.startsWith(INTERACTIVE_BOOK_CHAT_STORAGE_PREFIX) ||
          key === INTERACTIVE_BOOK_CHAT_ACTIVE_USER_SCOPE ||
          key === 'interactive-book-chat-anon-tab')
      ) {
        keys.push(key);
      }
    }
    for (const key of keys) {
      const value = sessionStorageApi.getItem(key);
      if (!value) continue;
      const targetKey =
        key === 'interactive-book-chat-anon-tab' ? INTERACTIVE_BOOK_CHAT_ANON_SCOPE_KEY : key;
      if (!local.getItem(targetKey)) {
        local.setItem(targetKey, value);
      }
      sessionStorageApi.removeItem(key);
    }
  } catch {
    // ignore migration failures
  }
}

if (typeof window !== 'undefined') {
  migrateLegacySessionStorageChat();
}

export function resolveInteractiveBookChatUserScope(
  localStorageApi: StorageLike | null = typeof localStorage !== 'undefined' ? localStorage : null,
  chatStorageApi: StorageLike | null = getChatStorage(),
): string {
  const userId = localStorageApi?.getItem('X_USER_ID')?.trim();
  if (userId) return userId;

  if (!chatStorageApi) return 'anonymous';

  let anon = chatStorageApi.getItem(INTERACTIVE_BOOK_CHAT_ANON_SCOPE_KEY)?.trim();
  if (!anon) {
    anon = `anon-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      chatStorageApi.setItem(INTERACTIVE_BOOK_CHAT_ANON_SCOPE_KEY, anon);
    } catch {
      return 'anonymous';
    }
  }
  return anon;
}

export function buildInteractiveBookChatStorageKey(
  userScope: string,
  scope: InteractiveBookChatSessionScope,
): string {
  return `${INTERACTIVE_BOOK_CHAT_STORAGE_PREFIX}:${userScope}:${scope.bookId}:${scope.interactionId}`;
}

export function truncateChatContent(content: string): string {
  const trimmed = content.trim();
  if (trimmed.length <= CHAT_SESSION_MAX_MESSAGE_CHARS) return trimmed;
  return trimmed.slice(0, CHAT_SESSION_MAX_MESSAGE_CHARS);
}

export function uiMessagesToPersisted(messages: SharedAiBuddyMessage[]): PersistedChatMessage[] {
  return messages
    .filter((message) => message.role === 'learner' || message.role === 'assistant')
    .map((message) => ({
      id: message.id,
      role: message.role === 'learner' ? ('user' as const) : ('assistant' as const),
      content: truncateChatContent(message.text),
      createdAt: new Date().toISOString(),
    }))
    .slice(-CHAT_SESSION_MAX_MESSAGES);
}

export function persistedToRestoredChat(messages: PersistedChatMessage[]): RestoredInteractiveBookChat {
  const uiMessages: SharedAiBuddyMessage[] = messages.map((message) => ({
    id: message.id,
    role: message.role === 'user' ? 'learner' : 'assistant',
    text: message.content,
  }));
  const apiHistory = messages.map((message) => ({
    role: message.role,
    content: message.content,
  }));
  return { uiMessages, apiHistory };
}

function isValidPersistedPayload(raw: unknown): raw is PersistedInteractiveBookChat {
  if (!raw || typeof raw !== 'object') return false;
  const record = raw as PersistedInteractiveBookChat;
  if (record.version !== 1) return false;
  if (typeof record.bookId !== 'string' || typeof record.interactionId !== 'string') return false;
  if (typeof record.userScope !== 'string' || typeof record.savedAt !== 'number') return false;
  if (!Array.isArray(record.messages)) return false;
  return record.messages.every(
    (message) =>
      message &&
      typeof message === 'object' &&
      typeof message.id === 'string' &&
      (message.role === 'user' || message.role === 'assistant') &&
      typeof message.content === 'string' &&
      typeof message.createdAt === 'string',
  );
}

export function parsePersistedInteractiveBookChat(raw: string | null): PersistedInteractiveBookChat | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!isValidPersistedPayload(parsed)) return null;
    if (Date.now() - parsed.savedAt > CHAT_SESSION_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function syncInteractiveBookChatUserScope(
  chatStorageApi: StorageLike | null = getChatStorage(),
  localStorageApi: StorageLike | null = typeof localStorage !== 'undefined' ? localStorage : null,
): string {
  const current = resolveInteractiveBookChatUserScope(localStorageApi, chatStorageApi);
  if (!chatStorageApi) return current;

  const previous = chatStorageApi.getItem(INTERACTIVE_BOOK_CHAT_ACTIVE_USER_SCOPE)?.trim();
  if (previous && previous !== current) {
    clearInteractiveBookChatSessionsForUserScope(previous, chatStorageApi);
  }
  try {
    chatStorageApi.setItem(INTERACTIVE_BOOK_CHAT_ACTIVE_USER_SCOPE, current);
  } catch {
    // ignore quota errors for scope tracker
  }
  return current;
}

export function clearInteractiveBookChatSessionsForUserScope(
  userScope: string,
  chatStorageApi: StorageLike | null = getChatStorage(),
): void {
  if (!chatStorageApi) return;
  const prefix = `${INTERACTIVE_BOOK_CHAT_STORAGE_PREFIX}:${userScope}:`;
  const keysToRemove: string[] = [];
  for (let index = 0; index < chatStorageApi.length; index += 1) {
    const key = chatStorageApi.key(index);
    if (key?.startsWith(prefix)) keysToRemove.push(key);
  }
  for (const key of keysToRemove) {
    try {
      chatStorageApi.removeItem(key);
    } catch {
      // ignore
    }
  }
}

/** Clear cached chat for the signed-out user; call during logout before X_USER_ID is removed. */
export function clearInteractiveBookChatOnSignOut(
  localStorageApi: StorageLike | null = typeof localStorage !== 'undefined' ? localStorage : null,
  chatStorageApi: StorageLike | null = getChatStorage(),
): void {
  if (!chatStorageApi) return;
  const userScope = localStorageApi?.getItem('X_USER_ID')?.trim();
  if (userScope) {
    clearInteractiveBookChatSessionsForUserScope(userScope, chatStorageApi);
  }
  try {
    chatStorageApi.removeItem(INTERACTIVE_BOOK_CHAT_ACTIVE_USER_SCOPE);
  } catch {
    // ignore
  }
}

export function loadPersistedInteractiveBookChat(
  scope: InteractiveBookChatSessionScope,
  chatStorageApi: StorageLike | null = getChatStorage(),
  localStorageApi: StorageLike | null = typeof localStorage !== 'undefined' ? localStorage : null,
): RestoredInteractiveBookChat | null {
  const userScope = syncInteractiveBookChatUserScope(chatStorageApi, localStorageApi);
  if (!chatStorageApi) return null;

  const key = buildInteractiveBookChatStorageKey(userScope, scope);
  const parsed = parsePersistedInteractiveBookChat(chatStorageApi.getItem(key));
  if (!parsed) return null;
  if (parsed.bookId !== scope.bookId || parsed.interactionId !== scope.interactionId) return null;
  if (parsed.userScope !== userScope) return null;

  return persistedToRestoredChat(parsed.messages);
}

export function savePersistedInteractiveBookChat(
  scope: InteractiveBookChatSessionScope,
  messages: SharedAiBuddyMessage[],
  chatStorageApi: StorageLike | null = getChatStorage(),
  localStorageApi: StorageLike | null = typeof localStorage !== 'undefined' ? localStorage : null,
): void {
  const userScope = syncInteractiveBookChatUserScope(chatStorageApi, localStorageApi);
  if (!chatStorageApi) return;

  const persistedMessages = uiMessagesToPersisted(messages);
  if (persistedMessages.length === 0) return;

  const payload: PersistedInteractiveBookChat = {
    version: 1,
    bookId: scope.bookId,
    interactionId: scope.interactionId,
    userScope,
    messages: persistedMessages,
    savedAt: Date.now(),
  };

  try {
    chatStorageApi.setItem(
      buildInteractiveBookChatStorageKey(userScope, scope),
      JSON.stringify(payload),
    );
  } catch {
    // ignore quota / private mode failures
  }
}

export function clearPersistedInteractiveBookChat(
  scope: InteractiveBookChatSessionScope,
  chatStorageApi: StorageLike | null = getChatStorage(),
  localStorageApi: StorageLike | null = typeof localStorage !== 'undefined' ? localStorage : null,
): void {
  const userScope = resolveInteractiveBookChatUserScope(localStorageApi, chatStorageApi);
  if (!chatStorageApi) return;
  try {
    chatStorageApi.removeItem(buildInteractiveBookChatStorageKey(userScope, scope));
  } catch {
    // ignore
  }
}
